import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import AccountAutocomplete from '../common/AccountAutocomplete';
import { AccountService } from '../../services/account.service';
import { EmployeeLoanService } from '../../services/employeeLoan.service';
import { useToast } from '../../context/ToastContext';
import { Account } from '../../interfaces/accounting.interface';
import { ILoanOpeningBalancePreview } from '../../interfaces/employeeLoan.interface';
import { leafAccounts } from '../../utils/postingAccounts';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

interface Props {
    open: boolean;
    institutionId: string;
    preview: ILoanOpeningBalancePreview;
    onClose: () => void;
    /** Called after the opening balance is posted. */
    onPosted: () => void;
}

// Accounting switched on after loans were paid out as a register: one journal brings the open
// balances into the books (Dr Employee loan receivable, Cr the account the user picks).
const LoanOpeningBalanceDialog: React.FC<Props> = ({ open, institutionId, preview, onClose, onPosted }) => {
    const { showSuccess, showApiError } = useToast();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [contraId, setContraId] = useState('');
    const [date, setDate] = useState(toLocalIsoDate());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setContraId('');
        setDate(toLocalIsoDate());
        AccountService.getAllAccounts()
            .then(all => setAccounts(Array.isArray(all) ? leafAccounts(all) : []))
            .catch(e => showApiError(e, 'Failed to load the chart of accounts'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const post = async () => {
        setSaving(true);
        try {
            await EmployeeLoanService.postOpeningBalance(institutionId, { contraAccountId: contraId, date });
            showSuccess('Loan opening balance posted');
            onPosted();
        } catch (e) {
            showApiError(e, 'Failed to post the loan opening balance');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" data-help-page="payroll.loans.openingBalance">
            <DialogTitle>Bring loans into the books</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                        {preview.loanCount} loan(s) were paid out while the Accounting module was off. One journal entry
                        debits the employee loan receivable account (from Payroll Settings) with their open balance
                        of {formatMoney(preview.total)} and credits the account you choose below.
                    </Typography>
                    <Alert severity="info">
                        Pick the account the money originally came from: usually the cash or bank account, or an
                        opening balance equity account if the cash book is also being opened now.
                    </Alert>
                    <AccountAutocomplete
                        data-help="payroll.loans.openingBalance.contra"
                        label="Credit account"
                        accounts={accounts}
                        value={contraId}
                        onChange={setContraId}
                        required
                    />
                    <TextField data-help="payroll.loans.openingBalance.date" label="Journal date" type="date" size="small" value={date}
                        onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: '100%', maxWidth: 300 }} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="payroll.loans.openingBalance.post" variant="contained" onClick={post} disabled={saving || !contraId}>
                    Post opening balance
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoanOpeningBalanceDialog;
